import { useState, useEffect, useCallback, useMemo } from "react";
import InventariosService from "../api/inventarios.api";
import { getFiltrosInventariosIniciales } from "../constants";
import { contarPorEstado, filtrarPorEstado } from "../helpers/estadisticas";

/**
 * Hook para orquestar el listado de inventarios (admin): filtros, estadísticas,
 * alta, edición y baja, recargando siempre desde InventariosService tras cada
 * mutación en vez de mutar el array local de forma optimista, porque el
 * snapshot de stock y el estado de cada lista los decide el backend,
 * no el cliente (ver tasks/milestones.md, decisión 3).
 *
 * DECISION: fechas, almacén y preparador se filtran en el servidor, que devuelve
 * TODO lo que cumple (sin paginar: con el mes por defecto son pocas listas). El
 * estado se filtra aquí, en cliente, para que las fichas cuenten sobre la lista
 * sin ese filtro y elegir una ficha no dispare otra petición.
 *
 * @returns {{
 *   inventarios: Array,
 *   estadisticas: {porEstado: Object<string, number>, total: number},
 *   loading: boolean,
 *   error: string|null,
 *   filtros: Object,
 *   aplicarFiltros: Function,
 *   crear: Function,
 *   actualizar: Function,
 *   eliminar: Function,
 *   recargar: Function
 * }} Estado y operaciones de inventarios
 */
export const useInventarios = () => {
    const [todos, setTodos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtros, setFiltros] = useState(getFiltrosInventariosIniciales);

    const { preparadorId, fechaDesde, fechaHasta } = filtros;
    const consultaServidor = useMemo(() => ({ preparadorId, fechaDesde, fechaHasta }), [preparadorId, fechaDesde, fechaHasta]);

    // No vacía la lista antes de tener el resultado: la tabla anterior se
    // conserva mientras el DataGrid/lista móvil pintan su propio `loading`
    const cargar = useCallback(async (consulta) => {
        setLoading(true);
        try {
            const data = await InventariosService.listar(consulta);
            setTodos(Array.isArray(data) ? data : []);
            setError(null);
        } catch (err) {
            console.error("Error al cargar inventarios:", err);
            setError(err.message || "No se pudieron cargar los inventarios. Intenta nuevamente.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargar(consultaServidor);
    }, [cargar, consultaServidor]);

    const inventarios = useMemo(() => filtrarPorEstado(todos, filtros.estado), [todos, filtros.estado]);
    const estadisticas = useMemo(() => contarPorEstado(todos), [todos]);

    /** Recarga el listado con los filtros actuales. */
    const recargar = useCallback(() => cargar(consultaServidor), [cargar, consultaServidor]);

    /** Sustituye los filtros de una vez (fichas, Filtrar, Limpiar). */
    const aplicarFiltros = useCallback((nuevosFiltros) => {
        setFiltros({ ...getFiltrosInventariosIniciales(), ...nuevosFiltros });
    }, []);

    // Los errores de crear/actualizar/eliminar NO se capturan aquí: se
    // relanzan para que la página los muestre como toast, sin mezclarlos con
    // `error` (que representa únicamente el fallo de carga del listado)
    const crear = useCallback(
        async (datos) => {
            const creado = await InventariosService.crear(datos);
            await cargar(consultaServidor);
            return creado;
        },
        [cargar, consultaServidor]
    );

    const actualizar = useCallback(
        async (id, datos) => {
            const actualizado = await InventariosService.actualizar(id, datos);
            await cargar(consultaServidor);
            return actualizado;
        },
        [cargar, consultaServidor]
    );

    const eliminar = useCallback(
        async (id) => {
            await InventariosService.eliminar(id);
            await cargar(consultaServidor);
        },
        [cargar, consultaServidor]
    );

    return { inventarios, estadisticas, loading, error, filtros, aplicarFiltros, crear, actualizar, eliminar, recargar };
};
